<template>
  <div>
    <div v-if="period === 'daily'">
      <daily-planification-modified
        :subject="subject"
        :grade="grade"
        :oa="oa"
        :ind="ind"
        :type="type"
        :condition="condition"
        :demo="isDemo"
        :demoStreamText="isDemo ? demoFixture.streamText : null"
        @done="singleSuccess"
        @seccion-lista="seccionLista"
      />
    </div>
    <div v-if="period === 'monthly'">
      <div v-for="(tag, tagidx) in tagsPile" :key="tagidx">
        <PlanificationOaTable
          :subject="subject"
          :grade="grade"
          :oa="tag"
          :type="type"
          :condition="condition"
          @tag-completed="addTag"
        />
      </div>
    </div>
    <b-row
      v-if="showDownload"
      class="w-100 download-button py-2 m-1"
      :class="download ? 'download-off' : 'download-on'"
      @click="downloadDocument()"
    >
      <b-col> Has terminado ¡Descarga tu archivo! </b-col>
      <b-col cols="2" class="col-sm-1">
        <v-icon :name="download ? 'download-white' : 'download'" />
      </b-col>
    </b-row>
    <FeedbackComponent v-if="showDownload && !isDemo" :code="2" />
    <!-- `showDownload` se enciende cuando la tabla termino de streamear. Antes
         esta seccion se montaba de entrada, asi que mientras la planificacion
         corria el profesor ya veia la botonera de colores del PPT y el estado
         vacio de las slides, compitiendo con el contenido que estaba leyendo. -->
    <SlidesOnlineComponent
      v-if="period == 'daily' && showDownload"
      id="slides"
      :slidesData="slidesOnlineData"
      :loading="loadingSlides"
      :sesionCaida="sesionCaida"
      @reingresar="reingresar"
      :dailyPlanification="dailyPlanification"
      :demo="isDemo"
      :demoSlides="isDemo ? demoFixture.slides : null"
      :demoPptUrl="isDemo ? demoFixture.downloads.pptx : null"
      @remake="remake"
      @slideUpdate="slideUpdate"
      @slideDone="slideDone"
      @revelada="slideRevelada"
      @download-word="downloadDocument"
    />
    <p
      v-if="isDemo"
      class="text-center text-muted small font-italic mt-2"
    >
      Planificación de ejemplo
    </p>
  </div>
</template>

<script>
import PlanificationOaTable from "./PlanificationOaTable.vue";
import DailyPlanification from "./DailyPlanification";
import DailyPlanificationModified from "./DailyPlanificationModified";
import chatBotService from "@/services/chatBotService";
import userService from "@/services/userService";
import FeedbackComponent from "./FeedbackComponent.vue";
import SlidesOnlineComponent from "./SlidesOnlineComponent.vue";
import assistedMaterialService from "@/services/assistedMaterialService";
import { scrollToElementTop } from "@/utils/scrollToTop";
import {
  renovarTicketsSlides,
  esSesionCaida,
} from "@/services/planificationApi";

// El mazo son 12 slides: la portada (indice 0, que trae el padre) y 11 mas.
const TOTAL_SLIDES = 12;
// Cuantas generar a la vez. Cada slide puede pedir hasta 3 imagenes, asi que
// subir esto le encima llamadas a Together y aparecen los 429.
const SLIDES_EN_PARALELO = 3;
// Cuanto espera la vitrina por una slide que no llega antes de seguir adelante.
const ESPERA_MAXIMA_MS = 45000;
// Cuanto se espera a que una slide avise que termino su efecto de aparicion.
//
// Tiene que quedar COMODAMENTE por encima de lo que dura la coreografia
// completa, o el vigilante corta a una lamina sana: 0,85s de armazon + el
// tecleo del texto + 0,45s + hasta 8s bajando la imagen + 0,55s. Con 15s se
// disparaba justo en las slides con imagen pesada y saltaba a la siguiente
// dejando la anterior sin foto.
const ESPERA_EFECTO_MS = 30000;
// Respiro entre una lamina y la siguiente.
const PAUSA_ENTRE_SLIDES_MS = 700;

// Que slide depende de que seccion de la planificacion. Sale de los prompts
// 100-112 (ver Prompt.actives): cada slide usa una sola seccion, salvo la
// ultima. Como el modelo entrega las secciones en orden, las slides 0 y 1 se
// pueden empezar apenas cierra `introduction`, que llega 3ra de 15 -- o sea a
// los pocos segundos, en vez de esperar la planificacion completa.
const SLIDES_POR_SECCION = {
  introduction: [0, 1],
  explanation: [2],
  worked_example: [3],
  concepts: [4],
  steps: [5],
  remember: [6],
  understand: [7],
  analyze: [8],
  apply: [9],
  evaluate: [10],
  motivation: [11],
};

// Función utilitaria para obtener el token CSRF de la cookie
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

export default {
  name: "PlanificationTable",
  components: {
    PlanificationOaTable,
    FeedbackComponent,
    DailyPlanification,
    DailyPlanificationModified,
    SlidesOnlineComponent,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
    condition: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      tagsPile: [],
      currentIndex: 1,
      planificationReport: [],
      download: false,
      showDownload: false,
      dailyPlanification: {},
      generatingPpt: false,
      loadingMessages: [
        "Generando títulos...",
        "Generando diapositivas...",
        "Generando imágenes...",
        "Generando textos...",
        "Generando PPT...",
        "Insertando imágenes...",
        "Insertando textos...",
      ],
      loadingMessagesIndex: 0,
      pptGenerated: false,
      generatedPptData: null,
      slidesOnlineData: { slides: [] },
      loadingSlides: false,
      // Cuantas slides completaron su generacion (llegan desordenadas).
      slidesTerminadas: 0,
      // Indices ya encargados, para no generar (ni pagar) dos veces.
      slidesPedidas: {},
      // BUFFER: lo que se va generando, desordenado y todavia invisible.
      slidesBuffer: [],
      // Indices cuya generacion termino del todo (texto + imagen).
      slidesListas: {},
      // Cursor de la VITRINA: la proxima slide que le toca aparecer.
      siguienteAMostrar: 0,
      revelando: false,
      vigilanteSlide: null,
      colaSlides: [],
      slidesEnVuelo: 0,
      // Promesa unica de renovacion de tickets: la generacion del mazo espera
      // a que resuelva antes de pedir la primera diapositiva.
      ticketsListos: null,
      sesionCaida: false,
    };
  },

  methods: {
    remake() {
      this.singleSuccess(this.dailyPlanification);
    },
    addTag(planification) {
      this.planificationReport.push(planification);
      if (this.currentIndex < this.tags.length) {
        this.tagsPile.push(this.tags[this.currentIndex]);
        this.currentIndex += 1;
      } else {
        userService.get_user_data().then((data) => {
          this.$store.commit("login/setPoints", data);
        });
        this.showDownload = true;
      }
    },
    /**
     * Genera una slide completa: su texto (en streaming) y despues su imagen.
     *
     * `planificacion` permite pasarle una version PARCIAL mientras la
     * planificacion todavia se esta escribiendo. Es seguro porque solo se llama
     * cuando la seccion que esta slide necesita ya cerro (ver SLIDES_POR_SECCION).
     */
    async getSingleSlide(index, planificacion) {
      const slideType = {
        1: "intro",
        2: "strategies",
        3: "case-study",
        4: "metamorphosis",
        5: "development",
        6: "exercise-multiple",
        7: "exercise-development",
        8: "case-analysis",
        9: "mystery-case",
        10: "truth-evaluation",
        11: "farewell",
      };

      try {
        const slideObj = {
          type: slideType[index],
        };
        // const csrftoken = getCookie("csrftoken");
        const responseBody = await assistedMaterialService.getSingleSlideOnline(
          JSON.stringify({
            planification_crudo: planificacion || this.dailyPlanification,
            index: index,
          })// ,
          // csrftoken
        );
        const reader = responseBody.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let done = false;
        while (!done) {
          const { done: streamDone, value } = await reader.read();
          done = streamDone;
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          // console.log("[SlidesOnline] Chunk recibido:", chunk);
          buffer += chunk;
          if (buffer.startsWith("json")) {
            buffer = buffer.replace("json", "");
          }
          try {
            // let slideObj = JSON.parse(buffer);
            let partialSlide = JSON.parse(buffer);
            Object.assign(slideObj, partialSlide);
            this.$set(this.slidesBuffer, index, slideObj);
            // this.slidesOnlineData.slides[index] = slideObj;
          } catch (e) {
            try {
              let formattedBuffer = this.formatBuffer(buffer);
              let partialSlide = JSON.parse(formattedBuffer);
              // let slideObj = JSON.parse(formattedBuffer);
              Object.assign(slideObj, partialSlide);
              this.$set(this.slidesBuffer, index, slideObj);
              // this.slidesOnlineData.slides[index] = slideObj;
            } catch (e) {
              console.warn("Invalid JSON at the moment");
            }
          }

          // console.log("estado buffer", buffer);
        }
        // Procesar el buffer final si quedó algo
        if (buffer.trim()) {
          // console.log("[SlidesOnline] Procesando buffer final:", buffer);
          try {
            const slideObj = JSON.parse(buffer);
            slideObj.type = slideType[index];
            this.$set(this.slidesBuffer, index, slideObj);
            // this.slidesOnlineData.slides[index] = slideObj;
            // console.log("[SlidesOnline] Slide final agregado:", slideObj);
          } catch (e) {
            // Ignorar error de parseo de línea final (puede estar vacía)
            console.warn(
              "[SlidesOnline] Error parseando buffer final:",
              buffer,
              e
            );
          }
        }
        // console.log(
        //   "[SlidesOnline] Estado final de slidesOnlineData:",
        //   this.slidesOnlineData
        // );
        assistedMaterialService
          .getSingleSlideImage({ slide_data: slideObj, index: index })
          .then((data) => {
            // console.log("imagen recibida para ", index, data);
            this.$set(this.slidesBuffer, index, data);
            this.marcarSlideLista(index);
          });
      } catch (err) {
        this.loadingSlides = false;
        // this.slidesOnlineData = { slides: [] };
        alert("Error de red al obtener slide online.");
        console.error("[SlidesOnline] Error en fetch:", err);
      }
    },
    scrollToPresentation() {
      const slides = document.getElementById("slides");
      // `block: "center"` centraba la seccion completa de slides, que mide
      // varias pantallas y en ese momento esta casi vacia: el salto dejaba al
      // profesor pasado la paleta, leyendo espacio en blanco, justo cuando
      // acababa de aparecer la tabla. Se sube al inicio de la seccion.
      scrollToElementTop(slides);
    },
    async singleSuccess(dailyPlanification) {
      this.dailyPlanification = dailyPlanification;
      this.dailyPlanification.oa = this.oa;
      this.dailyPlanification.ind = this.ind;
      this.dailyPlanification.type = this.type;
      this.showDownload = true;
      this.loadingSlides = true;
      // #slides recien existe despues de este render (ver v-if de arriba).
      this.$nextTick(() => this.scrollToPresentation());
      if (this.isDemo) {
        // Hero slide comes from the fixture; pushing the first placeholder
        // kicks off the demo SingleSlideComponent chain (see slideDone).
        this.$set(this.slidesOnlineData.slides, 0, this.demoFixture.slides[0]);
        this.slidesOnlineData.slides.push({});
        return;
      }
      // OJO: aca NO se reinicia `slidesOnlineData`. Las slides se empiezan a
      // generar mientras la planificacion todavia se escribe (ver seccionLista),
      // asi que a esta altura ya hay varias listas o en camino; vaciar el arreglo
      // las tiraria a la basura y las volveria a pagar.
      //
      // Con la planificacion cerrada se encarga lo que falte: las slides cuyas
      // secciones llegaron al final, y cualquiera que no se haya alcanzado a
      // disparar durante el stream.
      this.encolarPendientes(this.dailyPlanification);
    },
    slideUpdate(payload) {
      this.$set(this.slidesOnlineData.slides, payload.index, payload.slide);
    },
    /**
     * Una seccion de la planificacion termino de llegar: se encargan las slides
     * que dependian de ella, sin esperar a que termine el resto.
     */
    seccionLista({ seccion, planificacion }) {
      const indices = SLIDES_POR_SECCION[seccion];
      if (!indices) return;
      indices.forEach((i) => this.encolarSlide(i, planificacion));
    },
    /** Encola una slide si no se habia pedido antes. */
    encolarSlide(index, planificacion) {
      // El demo publico no genera nada: sus diapositivas vienen grabadas en el
      // fixture y las va revelando su propia cadena (ver singleSuccess y
      // slideDone). Igual pasa por aca porque reproduce el stream con el MISMO
      // parser que el real, asi que emite `seccion-lista` como si estuviera
      // generando. Sin esta guarda se pide un ticket al servidor principal, el
      // visitante es anonimo, vuelve 401 y `esSesionCaida` lo interpreta como
      // sesion vencida: al profesor que llega de la publicidad se le muestra
      // "Se cerro tu sesion" en un flujo donde nunca hubo sesion.
      if (this.isDemo) return;
      if (this.slidesPedidas[index]) return;
      this.slidesPedidas[index] = true;
      // No se reserva hueco en la vitrina: si se empujaran los 12 marcadores de
      // entrada, el profesor veria las 12 tarjetas vacias llenandose en el orden
      // en que termina cada generacion, que es desordenado. La vitrina las
      // muestra de a una y en orden (ver revelarSiguiente).
      this.colaSlides.push({ index, planificacion });
      this.bombearCola();
    },
    /** Mantiene hasta SLIDES_EN_PARALELO generandose a la vez. */
    /**
     * Pide tickets frescos una sola vez, antes de la primera diapositiva.
     *
     * Se hace aca y no al final: el mazo dura unos dos minutos y si la sesion
     * se vence a mitad, las diapositivas que faltan fallan una por una en
     * silencio. Arrancando con la ventana completa por delante eso no pasa; y
     * si la sesion ya venia caida, se detecta antes de generar nada.
     */
    /**
     * Recargar es lo mas simple y lo mas honesto: la aplicacion arranca de
     * nuevo, ve que no hay sesion y pide entrar. La planificacion ya generada
     * sigue disponible en el historial del profesor.
     */
    reingresar() {
      window.location.reload();
    },
    asegurarTickets() {
      if (!this.ticketsListos) {
        this.ticketsListos = renovarTicketsSlides().catch((error) => {
          if (esSesionCaida(error)) {
            this.sesionCaida = true;
            this.loadingSlides = false;
            this.colaSlides = [];
          }
          throw error;
        });
      }
      return this.ticketsListos;
    },
    bombearCola() {
      if (this.sesionCaida) return;
      // La primera vez se espera la renovacion; despues resuelve al instante.
      this.asegurarTickets().then(() => this.bombearColaAhora()).catch(() => {});
    },
    bombearColaAhora() {
      while (this.slidesEnVuelo < SLIDES_EN_PARALELO && this.colaSlides.length) {
        // Se genera en el orden en que se van a MOSTRAR, no en el que se
        // encolaron. El modelo entrega las secciones en un orden que no calza
        // con el de las slides (`steps` y `concepts` llegan antes que
        // `worked_example`, que es la slide 3), asi que sin esto la vitrina
        // quedaba esperando la 3 mientras la 4 y la 5 ya estaban hechas.
        this.colaSlides.sort((a, b) => a.index - b.index);
        const { index, planificacion } = this.colaSlides.shift();
        this.slidesEnVuelo += 1;
        Promise.resolve(this.getSingleSlide(index, planificacion))
          .catch((error) => {
            // La sesion tambien se puede caer A MITAD del mazo. Si pasa, se
            // corta la cola: seguir intentando solo produce una fila de 401
            // que el profesor nunca ve.
            if (esSesionCaida(error)) {
              this.sesionCaida = true;
              this.loadingSlides = false;
              this.colaSlides = [];
            }
          })
          .then(() => {
            this.slidesEnVuelo -= 1;
            this.slidesTerminadas += 1;
            // Que termine de generarse no significa que ya se vea: de apagar la
            // burbuja se encarga la vitrina cuando muestra la ultima.
            this.bombearColaAhora();
          });
      }
    },
    /** La generacion de una slide termino del todo: puede entrar a la vitrina. */
    marcarSlideLista(index) {
      this.$set(this.slidesListas, index, true);
      this.revelarSiguiente();
    },
    /**
     * Muestra la siguiente slide SI le toca y SI ya esta lista.
     *
     * La generacion va en paralelo y termina desordenada (la 6 puede estar antes
     * que la 2). Mostrarlas en ese orden es como leer una planificacion que
     * empieza por el cierre: la vitrina las entrega de a una, en orden, y solo
     * cuando estan completas. Mientras la que toca no este lista, se queda la
     * burbuja de acuarela.
     */
    revelarSiguiente() {
      if (this.revelando) {
        // Se esta revelando una: si su efecto de aparicion no avisa que
        // termino, `revelando` quedaria en true para siempre y la vitrina se
        // traba. El vigilante tambien cubre ese caso.
        this.armarVigilante(this.siguienteAMostrar - 1, true);
        return;
      }
      const i = this.siguienteAMostrar;
      if (i >= TOTAL_SLIDES) return;
      if (!this.slidesListas[i] || !this.slidesBuffer[i]) {
        // Todavia no le llega el turno a esta: se arma el vigilante por si su
        // generacion se cayo y nunca avisa. Sin esto la vitrina queda trabada
        // para siempre en la burbuja de acuarela.
        this.armarVigilante(i);
        return;
      }

      this.revelando = true;
      this.cancelarVigilante();
      this.slidesOnlineData.slides.push(this.slidesBuffer[i]);
      this.siguienteAMostrar += 1;

      // La portada no es un SingleSlideComponent y no avisa cuando termina de
      // revelarse, asi que se pasa a la siguiente tras una pausa corta.
      if (i === 0) {
        setTimeout(() => {
          this.revelando = false;
          this.revelarSiguiente();
        }, 900);
      }
    },
    /**
     * Si la slide que toca no aparece en ESPERA_MAXIMA_MS, se sigue adelante:
     * con lo que haya en el buffer si trajo algo, o saltandola si no trajo nada.
     * Vale mas un mazo con una slide menos que un mazo que nunca termina.
     */
    armarVigilante(index, esperandoEfecto) {
      if (this.vigilanteSlide) return;
      this.vigilanteSlide = setTimeout(() => {
        this.vigilanteSlide = null;

        if (esperandoEfecto) {
          // El efecto de aparicion nunca aviso que termino: se destraba y se
          // sigue con la siguiente.
          console.warn("[slides] la slide", index, "no aviso su aparicion; se sigue");
          this.revelando = false;
          this.revelarSiguiente();
          return;
        }

        if (this.siguienteAMostrar !== index) return; // ya avanzo sola
        if (this.slidesBuffer[index]) {
          console.warn("[slides] la slide", index, "tardo demasiado; se muestra incompleta");
          this.$set(this.slidesListas, index, true);
        } else {
          console.warn("[slides] la slide", index, "no llego; se salta");
          this.siguienteAMostrar += 1;
        }
        this.revelarSiguiente();
      }, esperandoEfecto ? ESPERA_EFECTO_MS : ESPERA_MAXIMA_MS);
    },
    cancelarVigilante() {
      if (this.vigilanteSlide) {
        clearTimeout(this.vigilanteSlide);
        this.vigilanteSlide = null;
      }
    },
    /** Un SingleSlideComponent termino su efecto de aparicion. */
    slideRevelada() {
      // Cancelar el vigilante del efecto: si quedara vivo podria disparar
      // encima del revelado siguiente y mostrar dos a la vez.
      this.cancelarVigilante();
      if (this.siguienteAMostrar >= TOTAL_SLIDES) {
        this.revelando = false;
        this.loadingSlides = false;
        // Recien ahora el mazo esta completo: se habilita el boton de continuar
        // del chat. Antes aparecia apenas se montaba la tabla, y apretarlo
        // avanzaba el chat dejando al profesor sin el PPT que ya pago.
        this.$emit("componente-listo");
        return;
      }
      // Respiro entre laminas. Sin esto, cuando el buffer va adelantado (que es
      // lo normal gracias al prefetch) las slides listas se encadenan una tras
      // otra y el profesor ve aparecer varias de golpe: se pierde justo la
      // sensacion de que se van construyendo.
      setTimeout(() => {
        this.revelando = false;
        this.revelarSiguiente();
      }, PAUSA_ENTRE_SLIDES_MS);
    },
    /** Encarga las que falten (al cerrar la planificacion no debe quedar ninguna). */
    encolarPendientes(planificacion) {
      for (let i = 0; i < TOTAL_SLIDES; i++) this.encolarSlide(i, planificacion);
    },
    slideDone(payload) {
      this.$set(this.slidesOnlineData.slides, payload.index, payload.slide);

      // En el flujo real la VITRINA es la que manda: ella decide cuando el mazo
      // esta completo (ver slideRevelada). Este manejador quedaba de la version
      // anterior y hacia dos daños: contaba `slidesTerminadas` una segunda vez
      // (ya se cuenta al generar, en bombearCola), con lo que el contador
      // llegaba al tope a mitad de camino y apagaba `loadingSlides` antes de
      // tiempo -- por eso los botones de descarga aparecian antes de que el PPT
      // estuviera listo. Y ademas llamaba a `abrirSlides`, que ya no existe.
      //
      // Se conserva solo la cadena del demo, que sigue empujando marcadores de
      // posicion de a uno.
      if (!this.isDemo) return;
      if (payload.index < TOTAL_SLIDES - 1) {
        this.slidesOnlineData.slides.push({});
      } else {
        this.loadingSlides = false;
        // Se le da un momento al visitante para ver el mazo terminado antes de
        // llevar el chat a su cierre.
        setTimeout(() => this.$emit("go-to-step", "closingMessage"), 2500);
      }
    },
    formatBuffer(buffer) {
      const pendingElements = [];
      let newBuffer = buffer;
      for (let i = 0; i < buffer.length; i++) {
        if (buffer[i] === "{") {
          pendingElements.push("{");
        }
        if (buffer[i] === "[") {
          pendingElements.push("[");
        }
        if (buffer[i] === '"') {
          if (
            pendingElements.length > 0 &&
            pendingElements[pendingElements.length - 1] === '"'
          ) {
            pendingElements.pop();
          } else {
            pendingElements.push('"');
          }
        }
        if (buffer[i] === "}") {
          if (
            pendingElements.length > 0 &&
            pendingElements[pendingElements.length - 1] === "{"
          ) {
            pendingElements.pop();
          }
        }
        if (buffer[i] === "]") {
          if (
            pendingElements.length > 0 &&
            pendingElements[pendingElements.length - 1] === "["
          ) {
            pendingElements.pop();
          }
        }
      }
      while (pendingElements.length > 0) {
        const lastElement = pendingElements.pop();
        if (lastElement === "{") {
          newBuffer += "}";
        } else if (lastElement === '"') {
          newBuffer += '"';
        } else if (lastElement === "[") {
          newBuffer += "]";
        }
      }
      return newBuffer;
    },
    async success(dailyPlanification) {
      this.dailyPlanification = dailyPlanification;
      this.dailyPlanification.oa = this.oa;
      this.dailyPlanification.ind = this.ind;
      this.dailyPlanification.type = this.type;
      this.showDownload = true;
      // --- Slides Online: Llamada automática al backend con stream ---
      this.loadingSlides = true;
      this.slidesOnlineData = { slides: [] };
      try {
        const csrftoken = getCookie("csrftoken");
        const response = await fetch(
          "/api/assisted_materials/get_slides_online/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-CSRFToken": csrftoken,
            },
            body: JSON.stringify({
              planification_crudo: this.dailyPlanification.text,
            }),
          }
        );
        // console.log("[SlidesOnline] Respuesta fetch:", response);
        if (!response.ok) {
          this.loadingSlides = false;
          this.slidesOnlineData = { slides: [] };
          alert("No se pudo obtener slides online: " + response.status);
          return;
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let done = false;
        while (!done) {
          const { done: streamDone, value } = await reader.read();
          done = streamDone;
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          // console.log("[SlidesOnline] Chunk recibido:", chunk);
          buffer += chunk;
          // Procesar por líneas (cada slide es una línea JSON)
          let lines = buffer.split("\n");
          buffer = lines.pop(); // la última puede estar incompleta
          for (let line of lines) {
            // console.log("[SlidesOnline] Procesando línea:", line);
            if (line.trim()) {
              try {
                const slideObj = JSON.parse(line);
                // console.log("slideObj", slideObj);
                this.slidesOnlineData.slides.push(slideObj.data);
                // console.log("[SlidesOnline] Slide agregado:", slideObj.data);
              } catch (e) {
                // Ignorar error de parseo de línea final (puede estar vacía)
                console.warn("[SlidesOnline] Error parseando línea:", line, e);
              }
            }
          }
        }
        // Procesar el buffer final si quedó algo
        if (buffer.trim()) {
          // console.log("[SlidesOnline] Procesando buffer final:", buffer);
          try {
            const slideObj = JSON.parse(buffer);
            this.slidesOnlineData.slides.push(slideObj.data);
            // console.log("[SlidesOnline] Slide final agregado:", slideObj.data);
          } catch (e) {
            // Ignorar error de parseo de línea final (puede estar vacía)
            console.warn(
              "[SlidesOnline] Error parseando buffer final:",
              buffer,
              e
            );
          }
        }
        // console.log(
        //   "[SlidesOnline] Estado final de slidesOnlineData:",
        //   this.slidesOnlineData
        // );
      } catch (err) {
        this.loadingSlides = false;
        this.slidesOnlineData = { slides: [] };
        alert("Error de red al obtener slides online.");
        console.error("[SlidesOnline] Error en fetch:", err);
      }
    },
    /**
     * Construye el objeto 'sections' esperado por el backend para descargas,
     * usando el mismo parseo que la visualización. Así el backend sigue funcionando
     * sin cambios y el equipo puede mantener compatibilidad.
     * Siempre retorna un objeto válido, aunque falten tags o secciones.
     */
    buildSectionsObject() {

      const text =
        this.dailyPlanification && this.dailyPlanification.text
          ? this.dailyPlanification.text
          : "";


      const inicioMatch = text.match(
        /\[inicio_clase\](.*?)(?=\[desarrollo_clase\]|$)/s
      );
      const desarrolloMatch = text.match(
        /\[desarrollo_clase\](.*?)(?=\[cierre_clase\]|$)/s
      );
      const cierreMatch = text.match(/\[cierre_clase\](.*)/s);

      function extractSubsections(sectionText, tags) {
        if (!sectionText) return [];
        return tags
          .map((tag) => {
            const regex = new RegExp(`\\[${tag}\\]`, "s");
            const match = sectionText.match(regex);
            if (match) {
              const contentRegex = new RegExp(
                `\\[${tag}\\](.*?)(?=\\[|$)`,
                "s"
              );
              const contentMatch = sectionText.match(contentRegex);
              return {
                name: tag,
                content: contentMatch ? contentMatch[1].trim() : "",
              };
            }
            return null;
          })
          .filter(Boolean);
      }

      const sectionNameMapping = {
        question: "Pregunta",
        question_relation: "Bajada de pregunta",
        introduction: "Introducción del objetivo",
        explanation: "Explicación",
        steps: "Pasos",
        errors: "Errores",
        concepts: "Conceptos",
        worked_example: "Ejemplo",
        remember: "Recordar",
        understand: "Entender",
        apply: "Aplicar",
        analyze: "Analizar",
        evaluate: "Evaluar",
        final_thoughts: "Reflexión final",
        motivation: "Despedida y motivación",
      };

      const sections = [];

      if (inicioMatch) {
        const subsections = extractSubsections(inicioMatch[1], [
          "question",
          "question_relation",
          "introduction",
        ]);
        const mappedSubsections = subsections.map((sub) => ({
          ...sub,
          name: sectionNameMapping[sub.name.toLowerCase()] || sub.name,
        }));
        sections.push(...mappedSubsections);
      }

      if (desarrolloMatch) {
        const subsections = extractSubsections(desarrolloMatch[1], [
          "explanation",
          "Pasos",
          "Errores",
          "Conceptos",
          "Ejemplo",
          "Recordar",
          "Entender",
          "Aplicar",
          "Analizar",
          "Evaluar",
        ]);
        const mappedSubsections = subsections.map((sub) => ({
          ...sub,
          name: sectionNameMapping[sub.name] || sub.name,
        }));
        sections.push(...mappedSubsections);
      }

      if (cierreMatch) {
        const subsections = extractSubsections(cierreMatch[1], [
          "final_thoughts",
          "motivation",
        ]);
        const mappedSubsections = subsections.map((sub) => ({
          ...sub,
          name: sectionNameMapping[sub.name] || sub.name,
        }));
        sections.push(...mappedSubsections);
      }

      const result = {};
      if (sections && sections.length > 0) {
        sections.forEach((section) => {
          if (section && section.name && section.content) {
            result[section.name] = section.content;
          }
        });
      }

      const requiredSections = [
        "Pregunta",
        "Explicación",
        "Ejemplo",
        "Pasos",
        "Errores",
        "Conceptos",
      ];
      requiredSections.forEach((sectionName) => {
        if (!result[sectionName]) {
          result[sectionName] = "Contenido no disponible";
        }
      });

      const wordData = {
        sections: [
          {
            name: "INICIO",
            subsections: [
              this.dailyPlanification.question,
              this.dailyPlanification.question_relation,
              this.dailyPlanification.introduction
            ],
          },
          {
            name: "DESARROLLO",
            subsections: [
              this.dailyPlanification.explanation,
              this.dailyPlanification.steps,
              this.dailyPlanification.errors,
              this.dailyPlanification.concepts,
              this.dailyPlanification.worked_example,
              this.dailyPlanification.remember,
              this.dailyPlanification.understand,
              this.dailyPlanification.apply,
              this.dailyPlanification.analyze,
              this.dailyPlanification.evaluate,
            ],
          },
          {
            name: "FINAL",
            subsections: [
              this.dailyPlanification.final_thoughts,
              this.dailyPlanification.motivation,
            ],
          },
        ],
        oa:
          this.dailyPlanification && this.dailyPlanification.oa
            ? this.dailyPlanification.oa
            : {},
        ind:
          this.dailyPlanification && this.dailyPlanification.ind
            ? this.dailyPlanification.ind
            : [],
        type:
          this.dailyPlanification && this.dailyPlanification.type
            ? this.dailyPlanification.type
            : "",
        ...result,
      };

      return wordData;
    },
    generatePptPlanification() {
      if (this.period !== "daily") return;
      this.generatingPpt = true;
      const loadingMessageInterval = setInterval(() => {
        this.loadingMessagesIndex =
          (this.loadingMessagesIndex + 1) % this.loadingMessages.length;
      }, 3000);
      // Usar el objeto correcto y plano para el backend
      const planification = this.buildSectionsObject();
      chatBotService
        .downloadPptPlanification({
          planification: planification,
        })
        .then((response) => {
          this.generatedPptData = response;
          this.pptGenerated = true;
          this.$toasted.success("¡Se ha generado el PPT!", {
            duration: 3000,
            position: "top-center",
          });
        })
        .catch(() => {
          this.$toasted.error(
            "Se ha producido un error al generar el PPT. Por favor, inténtalo de nuevo."
          );
        })
        .finally(() => {
          this.generatingPpt = false;
          clearInterval(loadingMessageInterval);
        });
    },
    downloadPptPlanification() {
      if (this.generatedPptData) {
        let filename = "";
        const disposition =
          this.generatedPptData.headers["Content-Disposition"];
        if (disposition && disposition.indexOf("attachment") !== -1) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(disposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }
        const blob = new Blob([this.generatedPptData.data], {
          type: this.generatedPptData.headers["content-type"],
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(link.href);
      } else {
        this.$toasted.error(
          "Se ha producido un error al generar el PPT. Por favor, inténtalo de nuevo."
        );
      }
    },
    downloadDocument() {
      if (this.isDemo) {
        // The demo serves a pre-generated DOCX; no backend involved.
        const link = document.createElement("a");
        link.href = this.demoFixture.downloads.docx;
        link.setAttribute("download", "");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      this.download = true;
      // Usar el objeto correcto y plano para el backend
      const planification = this.buildSectionsObject();

      chatBotService
        .downloadPlanification({
          type: this.type,
          period: this.period,
          planification: planification,
        })
        .then((response) => {
          var filename = "";
          var disposition = response.headers["Content-Disposition"];
          if (disposition && disposition.indexOf("attachment") !== -1) {
            var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            var matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, "");
            }
          }

          var blob = new Blob([response.data], {
            type: response.headers["content-type"],
          });

          var link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        })
        .catch((error) => {
          this.$toasted.error(
            "Error al descargar el documento: " +
              (error.response ? error.response.status : error.message)
          );
        })
        .finally(() => {
          this.download = false;
        });
    },
  },
  beforeDestroy() {
    this.cancelarVigilante();
  },
  mounted() {
    if (this.tags && this.tags.length) {
      this.tagsPile = [this.tags[0]];
    }
  },
  computed: {
    // Demo mode is signalled by the chat context itself (context.demo holds
    // the planificationDemoFixture), so ChatMessageComponent stays untouched.
    isDemo() {
      return !!(this.context && this.context.demo);
    },
    demoFixture() {
      return this.context.demo;
    },
    subject() {
      return this.context.asignatura.name;
    },
    grade() {
      return this.context.curso.original_name;
    },
    tags() {
      return this.context.indicadores;
    },
    type() {
      return this.context.type.option;
    },
    period() {
      return this.context.period.option;
    },
    oa() {
      if (this.context.single_oa) {
        return this.context.single_oa;
      } else {
        return false;
      }
    },
    ind() {
      if (this.context.single_indicadores) {
        return this.context.single_indicadores;
      } else {
        return [];
      }
    },
    reportContext() {
      return {
        ASIGNATURA: this.subject,
        NIVEL: this.grade,
        type: this.type,
        period: this.period,
        PROFESOR:
          this.$store.state.login.first_name +
          " " +
          this.$store.state.login.last_name,
        planification: this.dailyPlanification,
        oas: this.planificationReport.map((tag, tagidx) => {
          return {
            name: tag.name.replace(/<(.*?)>/g, ""),
            indicadores: tag.indicators.map((ind, indidx) => {
              return {
                name: this.tagsPile[tagidx].options[indidx].name,
                actividades: ind.activities.map((activity) => {
                  return {
                    actividad: activity.text,
                    estrategia: activity.dua,
                    titulo: activity.title,
                  };
                }),
              };
            }),
          };
        }),
      };
    },
  },
};
</script>

<style>
/* CONTINUE BUTTON */
.download-button {
  border-radius: 5px;
  border: 2pt solid rgb(0, 0, 0) !important;
  font-family: "Ubuntu Medium", Arial, sans-serif;
  font-weight: bold; /* Aplica negrita al texto */
}
.download-on {
  background-color: rgb(255, 255, 255);
  cursor: pointer;
}
.download-off {
  background-color: rgb(0, 185, 174);

  cursor: not-allowed;
}
</style>
